import get from 'lodash/get';
import moment from 'moment';

import type { TranslationAPI } from '@lumapps/translations';
import { getElapsedTimeFromUtcDate } from '@lumapps/utils/time/getElapsedTimeFromUtcDate';

import { type LegacyNotification, NotificationContentType } from '../types';

const getContentTitle = (translation: TranslationAPI, notification?: LegacyNotification): string => {
    if (notification && notification.contentDetails) {
        return translation.translateObject(notification.contentDetails.title) || '';
    }

    return '';
};

const getContentType = (notification?: LegacyNotification): string => notification?.contentDetails?.type || '';

const isTheContentAnArticle = (notification?: LegacyNotification): boolean =>
    getContentType(notification) === NotificationContentType.article;

const isTheContentAnEvent = (notification?: LegacyNotification): boolean =>
    getContentType(notification) === NotificationContentType.event;

const isTheContentAPost = (notification?: LegacyNotification): boolean =>
    getContentType(notification) === NotificationContentType.post;

const isTheContentAPage = (notification?: LegacyNotification): boolean =>
    getContentType(notification) !== NotificationContentType.post &&
    getContentType(notification) !== NotificationContentType.community;

const isTheContentAPlayVideo = (notification?: LegacyNotification): boolean =>
    getContentType(notification) === NotificationContentType.playVideo;

const getFormattedContentEndDate = (notification?: LegacyNotification): string => {
    if (notification && notification.contentDetails) {
        const { endDate } = notification.contentDetails;

        return getElapsedTimeFromUtcDate(endDate, true);
    }

    return '';
};

const getParentContentTitle = (translation: TranslationAPI, notification?: LegacyNotification): string => {
    if (notification && notification.parentContentDetails) {
        return translation.translateObject(notification.parentContentDetails.title) || '';
    }

    return '';
};

const getParentInstanceTitle = (translation: TranslationAPI, notification?: LegacyNotification): string => {
    if (notification && notification.instanceDetails) {
        return (
            translation.translateObject(notification.instanceDetails.title) || notification.instanceDetails.name || ''
        );
    }

    return '';
};

const getContextTitle = (translation: TranslationAPI, notification?: LegacyNotification): string => {
    if (notification) {
        return translation.translateObject(notification.title) || '';
    }

    return '';
};

const getContextDescription = (translation: TranslationAPI, notification?: LegacyNotification): string => {
    if (notification) {
        return translation.translateObject(notification.description || {}) || '';
    }

    return '';
};

const getContentPostDetailTitle = (notification?: LegacyNotification): string =>
    get(notification, 'contentDetails.postStatusDetails.title') || '';

const isContentExpired = (notification?: LegacyNotification) => {
    const endDate = notification?.contentDetails?.endDate;
    return Boolean(endDate) && moment.utc(endDate).local().isBefore();
};

export {
    getContentPostDetailTitle,
    getContentTitle,
    getContentType,
    getContextDescription,
    getContextTitle,
    getFormattedContentEndDate,
    getParentContentTitle,
    getParentInstanceTitle,
    isContentExpired,
    isTheContentAnArticle,
    isTheContentAnEvent,
    isTheContentAPage,
    isTheContentAPlayVideo,
    isTheContentAPost,
};
